/**
 * CostCentersTab — CON-02: Centros de Costo
 */
import { useState, useEffect } from 'react'
import { Plus, Loader2, Building2, Pencil, Power } from 'lucide-react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'
import {
  useCostCenters, useCreateCostCenter, useUpdateCostCenter,
  type CostCenter,
} from '../hooks/useAccounting'

interface CostCenterFormProps {
  cc?: CostCenter | null
  allCenters: CostCenter[]
  onClose: () => void
}

function CostCenterForm({ cc, allCenters, onClose }: CostCenterFormProps) {
  const isEdit = !!cc

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [code, setCode] = useState(cc?.code ?? '')
  const [name, setName] = useState(cc?.name ?? '')
  const [parent, setParent] = useState(cc?.parent ?? '')
  const [centerType, setCenterType] = useState(cc?.center_type ?? 'cost')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateCostCenter()
  const update = useUpdateCostCenter(cc?.id ?? '')
  const isPending = create.isPending || update.isPending

  const handleSave = () => {
    if (!code || !name) return
    setError(null)
    const payload = { code, name, parent: parent || null, center_type: centerType }
    const cb = {
      onSuccess: onClose,
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setError(err.response?.data?.error?.message ?? 'Error al guardar')
      },
    }
    if (isEdit) update.mutate(payload, cb)
    else create.mutate(payload, cb)
  }

  const parentOptions = allCenters.filter(c => c.id !== cc?.id && c.is_active)

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'} size="sm"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !code || !name}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Guardar Cambios' : 'Crear Centro'}
        </button>
      </>}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="costcenterstab-codigo" className="block text-xs font-medium text-content-muted mb-1">Código *</label>
          <input id="costcenterstab-codigo"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="ej. ADM-001"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="costcenterstab-nombre" className="block text-xs font-medium text-content-muted mb-1">Nombre *</label>
          <input id="costcenterstab-nombre"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="ej. Administración"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="costcenterstab-tipo-de-centro" className="block text-xs font-medium text-content-muted mb-1">Tipo de centro</label>
          <select id="costcenterstab-tipo-de-centro"
            value={centerType}
            onChange={e => setCenterType(e.target.value as CostCenter['center_type'])}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="cost">Centro de costo — solo gastos</option>
            <option value="revenue">Centro de ingreso — vende, no controla el costo</option>
            <option value="profit">Centro de beneficio — se le mide margen</option>
          </select>
          <p className="mt-1 text-[11px] text-content-subtle">
            Define qué se le puede medir. A un área que no vende no se le exige margen:
            su gasto es estructura.
          </p>
        </div>
        <div>
          <label htmlFor="costcenterstab-centro-padre" className="block text-xs font-medium text-content-muted mb-1">Centro Padre</label>
          <select id="costcenterstab-centro-padre"
            value={parent}
            onChange={e => setParent(e.target.value)}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="">Sin centro padre</option>
            {parentOptions.map(c => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>
    </Modal>
  )
}

/**
 * Editar y desactivar/reactivar. Componente propio porque los hooks son de la
 * celda, no de la tabla. Desactivar se permite aunque el centro tenga
 * movimientos: no los toca. Un error inesperado lo avisa la red global de
 * errores de mutación.
 */
function AccionesDeCentro({ cc, onEdit }: { cc: CostCenter; onEdit: () => void }) {
  const update = useUpdateCostCenter(cc.id)
  const { confirm, modalProps } = useConfirmModal()

  const alternar = () => confirm({
    message: cc.is_active
      ? `¿Desactivar el centro ${cc.code} — ${cc.name}? Deja de ofrecerse para imputar y de heredarse como centro por defecto; sus movimientos se conservan.`
      : `¿Reactivar el centro ${cc.code} — ${cc.name}?`,
    variant: cc.is_active ? 'warning' : 'success',
    confirmLabel: cc.is_active ? 'Desactivar' : 'Reactivar',
    onConfirm: () => new Promise<void>(resolve => {
      update.mutate({ is_active: !cc.is_active }, { onSettled: () => resolve() })
    }),
  })

  return (
    <div className="flex justify-end gap-1">
      <button
        onClick={onEdit}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft"
      >
        <Pencil size={10} />Editar
      </button>
      <button
        onClick={alternar}
        disabled={update.isPending}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft disabled:opacity-50"
      >
        <Power size={10} />{cc.is_active ? 'Desactivar' : 'Reactivar'}
      </button>
      <ConfirmModal {...modalProps} />
    </div>
  )
}

export function CostCentersTab() {
  const [filterActive, setFilterActive] = useState('true')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CostCenter | null>(null)

  const { data, isLoading } = useCostCenters({ is_active: filterActive || undefined })
  const centers = data?.cost_centers ?? []
  const filtered = centers.filter(cc =>
    !search ||
    cc.code.toLowerCase().includes(search.toLowerCase()) ||
    cc.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <input aria-label="Buscar por código o nombre"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre..."
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary w-48"
          />
          <select aria-label="Estado"
            value={filterActive}
            onChange={e => setFilterActive(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos</option>
            <option value="true">Solo Activos</option>
            <option value="false">Solo Inactivos</option>
          </select>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true) }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
        >
          <Plus size={14} />Nuevo Centro
        </button>
      </div>

      <DataTable
        rows={filtered}
        rowKey={cc => cc.id}
        loading={isLoading}
        minWidth={760}
        caption="Centros de costo de la empresa"
        empty={
          centers.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="Sin centros de costo configurados"
              action={
                <button
                  onClick={() => { setEditing(null); setModalOpen(true) }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
                >
                  <Plus size={14} />Crear primer centro
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={Building2}
              size="md"
              title="Ningun centro coincide con el filtro"
              hint="Revisar el texto buscado o el filtro de estado."
            />
          )
        }
        columns={[
          {
            key: 'code', header: 'Código', sortable: true, width: 140,
            render: cc => <span className="font-mono text-content">{cc.code}</span>,
          },
          {
            key: 'name', header: 'Nombre', sortable: true,
            render: cc => (
              <span className="text-content">
                {cc.parent_code && <span className="text-xs text-content-subtle mr-1">&#8627;</span>}
                {cc.name}
              </span>
            ),
          },
          {
            key: 'parent_code', header: 'Padre', sortable: true,
            render: cc => <span className="font-mono text-content-muted">{cc.parent_code ?? '—'}</span>,
          },
          {
            key: 'is_active', header: 'Estado', sortable: true,
            sortValue: cc => (cc.is_active ? 1 : 0),
            render: cc => (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                cc.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'
              }`}>
                {cc.is_active ? 'Activo' : 'Inactivo'}
              </span>
            ),
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 200,
            render: cc => (
              <AccionesDeCentro cc={cc} onEdit={() => { setEditing(cc); setModalOpen(true) }} />
            ),
          },
        ]}
      />

      {modalOpen && (
        <CostCenterForm
          cc={editing}
          allCenters={centers}
          onClose={() => { setModalOpen(false); setEditing(null) }}
        />
      )}
    </div>
  )
}
